/**
 * Drawing parts for the hideout building generator: walls (planks, logs, stone), roofs (shed thatch, gable shingle,
 * gable slate, upturned hull), a door opening with warm interior light, a shuttered window that leaks light, and a
 * stovepipe or stone chimney. Each returns markup in frame pixels; `building.ts` lays them out per variant.
 */
import { el, mix, n, poly, type Point, type ResolvedPalette } from '../pieces/svg';

export interface Rect { x: number; y: number; w: number; h: number }
type Rng = (channel: string) => number;
const STROKE = 1.3;

export function footing(roles: ResolvedPalette, r: Rect, rng: Rng): string {
  let out = el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: roles.stoneShadow, stroke: roles.outline, 'stroke-width': STROKE });
  let x = r.x + 1;
  for (let i = 0; x < r.x + r.w - 4; i++) {
    const w = 14 + rng(`foot.${i}`) * 16;
    const ww = Math.min(w, r.x + r.w - 1 - x);
    out += el('rect', { x, y: r.y + 2 + (i % 2) * 1.5, width: ww - 1.5, height: r.h - 4 - (i % 2) * 1.5, rx: 3,
      fill: i % 3 === 0 ? roles.stoneLight : roles.stoneBase, stroke: roles.outlineSoft, 'stroke-width': 0.8 });
    x += w;
  }
  return out;
}

export function planks(roles: ResolvedPalette, r: Rect, rng: Rng, wear: number): string {
  let out = el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: roles.woodBase });
  const count = Math.round(r.w / 12);
  const pw = r.w / count;
  for (let i = 0; i < count; i++) {
    const x = r.x + i * pw;
    const tone = rng(`plank.${i}`);
    const fill = tone < 0.3 ? roles.woodShadow : tone < 0.75 ? roles.woodBase : roles.woodLight;
    out += el('rect', { x, y: r.y + (tone - 0.5) * 2, width: pw, height: r.h - (tone - 0.5) * 2, fill: mix(fill, roles.woodBase, 0.35) });
    out += el('path', { d: `M${n(x)} ${n(r.y)}V${n(r.y + r.h)}`, stroke: roles.woodShadow, 'stroke-width': 0.9 });
    if (rng(`plank.salt.${i}`) < wear) {
      const y = r.y + 6 + rng(`plank.salt.y.${i}`) * (r.h - 20);
      out += el('path', { d: `M${n(x + 3)} ${n(y)}v${n(8 + rng(`plank.salt.l.${i}`) * 10)}`, stroke: roles.salt, 'stroke-width': 1, opacity: 0.55 });
    }
  }
  // A horizontal batten keeps it reading as a built wall, not a fence.
  out += el('rect', { x: r.x, y: r.y + r.h * 0.42, width: r.w, height: 4, fill: roles.woodShadow, opacity: 0.85 });
  return out + el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: 'none', stroke: roles.outline, 'stroke-width': STROKE });
}

export function logs(roles: ResolvedPalette, r: Rect, rng: Rng): string {
  const rows = Math.round(r.h / 13);
  const lh = r.h / rows;
  let out = '';
  for (let i = 0; i < rows; i++) {
    const y = r.y + i * lh;
    const fill = rng(`log.${i}`) < 0.5 ? roles.woodBase : mix(roles.woodBase, roles.woodLight, 0.4);
    out += el('rect', { x: r.x - 4, y, width: r.w + 8, height: lh, rx: lh / 2, fill, stroke: roles.outline, 'stroke-width': 1 });
    out += el('path', { d: `M${n(r.x + 6)} ${n(y + lh * 0.35)}H${n(r.x + r.w - 6)}`, stroke: roles.woodShadow, 'stroke-width': 0.8, opacity: 0.6 });
    for (const end of [r.x - 4 + lh / 2, r.x + r.w + 4 - lh / 2]) {
      out += el('circle', { cx: end, cy: y + lh / 2, r: lh / 2 - 1, fill: roles.woodLight, stroke: roles.woodShadow, 'stroke-width': 0.8 });
    }
  }
  return out;
}

export function stone(roles: ResolvedPalette, r: Rect, rng: Rng): string {
  let out = el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: roles.stoneShadow });
  const rows = Math.round(r.h / 14);
  const rh = r.h / rows;
  for (let i = 0; i < rows; i++) {
    let x = r.x - (i % 2) * 9;
    for (let j = 0; x < r.x + r.w; j++) {
      const w = 18 + rng(`stone.${i}.${j}`) * 10;
      const x0 = Math.max(x, r.x), x1 = Math.min(x + w, r.x + r.w);
      if (x1 - x0 > 3) {
        out += el('rect', { x: x0 + 0.8, y: r.y + i * rh + 0.8, width: x1 - x0 - 1.6, height: rh - 1.6, rx: 2,
          fill: rng(`stone.t.${i}.${j}`) < 0.3 ? roles.stoneLight : roles.stoneBase });
      }
      x += w;
    }
  }
  return out + el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: 'none', stroke: roles.outline, 'stroke-width': STROKE });
}

export interface RoofArt { markup: string; edge: Point[] }

/** Single-pitch reed-thatch roof, high on the left, sagging in the middle. */
export function shedThatch(roles: ResolvedPalette, left: Point, right: Point, thick: number, sag: number, rng: Rng): RoofArt {
  const mid = { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 + sag };
  const top = `M${n(left.x)} ${n(left.y)}Q${n(mid.x)} ${n(mid.y)} ${n(right.x)} ${n(right.y)}`;
  const shape = `${top}L${n(right.x)} ${n(right.y + thick)}Q${n(mid.x)} ${n(mid.y + thick)} ${n(left.x)} ${n(left.y + thick)}Z`;
  let markup = el('path', { d: shape, fill: roles.reedBase, stroke: roles.outline, 'stroke-width': 1.5 });
  const at = (t: number, off: number) => {
    const u = 1 - t;
    return { x: u * u * left.x + 2 * u * t * mid.x + t * t * right.x, y: u * u * left.y + 2 * u * t * mid.y + t * t * right.y + off };
  };
  for (let i = 0; i < 46; i++) {
    const t = (i + 0.5) / 46;
    const a = at(t, 2 + rng(`thatch.${i}`) * 3), b = at(t + 0.008, thick - 1 - rng(`thatch.b.${i}`) * 3);
    markup += el('path', { d: `M${n(a.x)} ${n(a.y)}L${n(b.x)} ${n(b.y)}`, stroke: i % 3 ? roles.reedShadow : roles.reedLight, 'stroke-width': 1.1, opacity: 0.8 });
  }
  // Ragged reed fringe hanging off the eave, so it reads as thatch rather than a board.
  let fringe = '';
  for (let i = 0; i < 40; i++) {
    const t = (i + 0.3) / 40, p = at(t, thick - 1), len = 4 + rng(`fringe.${i}`) * 6;
    fringe += `M${n(p.x - 2.2)} ${n(p.y)}L${n(p.x + rng(`fringe.x.${i}`) * 2 - 1)} ${n(p.y + len)}L${n(p.x + 2.2)} ${n(p.y)}`;
  }
  markup += el('path', { d: fringe, fill: roles.reedShadow, stroke: roles.outline, 'stroke-width': 0.6 });
  markup += el('path', { d: top, stroke: roles.reedLight, 'stroke-width': 1.4, fill: 'none', opacity: 0.7 });
  const edge = [0.12, 0.34, 0.58, 0.82].map(t => at(t, thick));
  return { markup, edge };
}

/** Two-pitch roof with shingle or slate courses. */
export function gable(roles: ResolvedPalette, x0: number, x1: number, eaveY: number, apexY: number, slate: boolean): RoofArt {
  const apex = { x: (x0 + x1) / 2, y: apexY };
  const fill = slate ? roles.stoneShadow : roles.woodShadow;
  let markup = el('path', { d: poly([{ x: x0, y: eaveY }, apex, { x: x1, y: eaveY }]), fill, stroke: roles.outline, 'stroke-width': 1.6 });
  for (let i = 1; i < 6; i++) {
    const t = i / 6, y = apexY + (eaveY - apexY) * t;
    const dx = (apex.x - x0) * t;
    markup += el('path', { d: `M${n(apex.x - dx)} ${n(y)}H${n(apex.x + dx)}`, stroke: slate ? roles.stoneBase : roles.woodBase, 'stroke-width': 1.4, opacity: 0.8 });
  }
  markup += el('path', { d: `M${n(x0)} ${n(eaveY)}L${n(apex.x)} ${n(apexY)}L${n(x1)} ${n(eaveY)}`, stroke: slate ? roles.stoneLight : roles.woodLight, 'stroke-width': 1, fill: 'none', opacity: 0.6 });
  // Edge points sit on the eave line, where ivy and vines would hang from.
  const edge = [0.1, 0.3, 0.7, 0.9].map(t => ({ x: x0 + (x1 - x0) * t, y: eaveY }));
  return { markup, edge };
}

/** An upturned boat hull used as a roof: curved planked shell with a keel on top. */
export function hull(roles: ResolvedPalette, x0: number, x1: number, baseY: number, height: number): RoofArt {
  const shell = `M${n(x0)} ${n(baseY)}C${n(x0 + 10)} ${n(baseY - height)} ${n(x1 - 10)} ${n(baseY - height)} ${n(x1)} ${n(baseY)}Z`;
  let markup = el('path', { d: shell, fill: roles.woodBase, stroke: roles.outline, 'stroke-width': 1.6 });
  for (let i = 1; i < 5; i++) {
    const t = i / 5;
    markup += el('path', { d: `M${n(x0 + 8 * t)} ${n(baseY - 2)}C${n(x0 + 14)} ${n(baseY - height * t)} ${n(x1 - 14)} ${n(baseY - height * t)} ${n(x1 - 8 * t)} ${n(baseY - 2)}`,
      stroke: roles.woodShadow, 'stroke-width': 1, fill: 'none', opacity: 0.8 });
  }
  // Keel and gunwale follow the shell outline itself.
  markup += el('path', { d: `M${n(x0 + 2)} ${n(baseY - 1)}C${n(x0 + 10)} ${n(baseY - height)} ${n(x1 - 10)} ${n(baseY - height)} ${n(x1 - 2)} ${n(baseY - 1)}`,
    stroke: roles.woodShadow, 'stroke-width': 3.2, fill: 'none' });
  markup += el('path', { d: `M${n(x0 + 4)} ${n(baseY)}H${n(x1 - 4)}`, stroke: roles.clothBlueShadow, 'stroke-width': 3 });
  const edge = [0.15, 0.38, 0.62, 0.85].map(t => ({ x: x0 + (x1 - x0) * t, y: baseY - 1 }));
  return { markup, edge };
}

/** A door opening: dark interior lit warm from below, timber frame, optional arch. */
export function doorOpening(roles: ResolvedPalette, d: Rect, arched: boolean, scope: string): { defs: string; markup: string } {
  const id = `${scope}_doorlight`;
  const defs = el('linearGradient', { id, x1: 0, y1: 0, x2: 0, y2: 1 },
    el('stop', { offset: 0, 'stop-color': mix(roles.outline, '#000000', 0.35) })
    + el('stop', { offset: 0.55, 'stop-color': mix(roles.outline, roles.woodShadow, 0.5) })
    + el('stop', { offset: 1, 'stop-color': mix(roles.warmCore, roles.woodBase, 0.35) }));
  const r = d.w / 2;
  const shape = arched
    ? `M${n(d.x)} ${n(d.y + d.h)}V${n(d.y + r)}A${n(r)} ${n(r)} 0 0 1 ${n(d.x + d.w)} ${n(d.y + r)}V${n(d.y + d.h)}Z`
    : poly([{ x: d.x, y: d.y + d.h }, { x: d.x, y: d.y }, { x: d.x + d.w, y: d.y }, { x: d.x + d.w, y: d.y + d.h }]);
  let markup = el('path', { d: shape, fill: `url(#${id})`, stroke: roles.outline, 'stroke-width': 1.4 });
  if (!arched) {
    markup += el('rect', { x: d.x - 5, y: d.y - 6, width: d.w + 10, height: 6, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 1 });
    markup += el('rect', { x: d.x - 4, y: d.y, width: 4, height: d.h, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 0.8 });
    markup += el('rect', { x: d.x + d.w, y: d.y, width: 4, height: d.h, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 0.8 });
  }
  // Two low steps lead down into the room beneath the road.
  markup += el('rect', { x: d.x + 4, y: d.y + d.h - 7, width: d.w - 8, height: 3, fill: roles.stoneShadow, opacity: 0.8 });
  return { defs, markup };
}

/** Shuttered window: one shutter ajar, warm light leaking through the slats and the gap. */
export function shutteredWindow(roles: ResolvedPalette, w: Rect): string {
  let out = el('rect', { x: w.x, y: w.y, width: w.w, height: w.h, fill: mix(roles.warmCore, roles.paperBase, 0.25), stroke: roles.outline, 'stroke-width': 1.2 });
  const half = w.w / 2;
  // Left shutter closed with slats; right shutter swung ajar (narrower, angled look).
  out += el('rect', { x: w.x, y: w.y, width: half, height: w.h, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 1 });
  for (let y = w.y + 4; y < w.y + w.h - 2; y += 5) {
    out += el('path', { d: `M${n(w.x + 2)} ${n(y)}H${n(w.x + half - 2)}`, stroke: roles.warmCore, 'stroke-width': 1.1, opacity: 0.85 });
  }
  out += el('path', { d: poly([{ x: w.x + w.w, y: w.y }, { x: w.x + w.w + half * 0.55, y: w.y - 3 }, { x: w.x + w.w + half * 0.55, y: w.y + w.h + 3 }, { x: w.x + w.w, y: w.y + w.h }]),
    fill: roles.woodBase, stroke: roles.outline, 'stroke-width': 1 });
  out += el('rect', { x: w.x - 4, y: w.y + w.h, width: w.w + 8, height: 4, fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 0.9 });
  return out;
}

export function stovepipe(roles: ResolvedPalette, x: number, baseY: number, topY: number): string {
  return el('rect', { x: x - 3.5, y: topY, width: 7, height: baseY - topY, fill: roles.outline, stroke: roles.outline, 'stroke-width': 0.8 })
    + el('path', { d: `M${n(x - 7)} ${n(topY)}H${n(x + 7)}L${n(x + 4)} ${n(topY - 5)}H${n(x - 4)}Z`, fill: roles.outline })
    + el('path', { d: `M${n(x - 1.5)} ${n(topY + 3)}V${n(baseY - 2)}`, stroke: roles.outlineSoft, 'stroke-width': 1, opacity: 0.6 });
}

export function stoneChimney(roles: ResolvedPalette, x: number, baseY: number, topY: number): string {
  let out = el('rect', { x: x - 9, y: topY, width: 18, height: baseY - topY, fill: roles.stoneBase, stroke: roles.outline, 'stroke-width': 1.2 });
  for (let y = topY + 6; y < baseY; y += 7) out += el('path', { d: `M${n(x - 9)} ${n(y)}H${n(x + 9)}`, stroke: roles.stoneShadow, 'stroke-width': 0.9 });
  return out + el('rect', { x: x - 11, y: topY - 3, width: 22, height: 4, fill: roles.stoneShadow, stroke: roles.outline, 'stroke-width': 1 });
}
